import * as THREE from 'three'

/**
 * 仮素材のページテクスチャをランタイム生成する canvas ファクトリ。
 * 実画像に差し替える際は sources.js に texture エントリを追加し、
 * resources.items.xxx をそのまま Page に渡せばよい（このファイルは不要になる）。
 */

// ページ比率 1.28 : 1.71 に合わせたキャンバスサイズ
const TEX_WIDTH = 512
const TEX_HEIGHT = 684

function createCanvasTexture(draw, width = TEX_WIDTH, height = TEX_HEIGHT) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  draw(canvas.getContext('2d'))

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 4
  return texture
}

// 折り返し: measureText で幅を測りながら行を分割する。
// 英単語（ASCII の連続）は途中で切らず、日本語は1文字単位で折り返す
function wrapText(ctx, text, maxWidth) {
  const tokens = text.match(/\n|[!-~]+ ?|./g) ?? []
  const lines = []
  let line = ''
  for (const token of tokens) {
    if (token === '\n') {
      lines.push(line.trimEnd())
      line = ''
      continue
    }
    if (line !== '' && ctx.measureText(line + token).width > maxWidth) {
      lines.push(line.trimEnd())
      line = token
    } else {
      line += token
    }
  }
  if (line !== '') lines.push(line.trimEnd())
  return lines
}

// 中ページ: 紙色の背景＋大きなページ番号（めくり動作の視認用）
export function createPageTexture({ label, bgColor = '#f5efe0', accentColor = '#8a7a5c' }) {
  return createCanvasTexture((ctx) => {
    ctx.fillStyle = bgColor
    ctx.fillRect(0, 0, TEX_WIDTH, TEX_HEIGHT)

    // 縁取り
    ctx.strokeStyle = accentColor
    ctx.lineWidth = 6
    ctx.strokeRect(24, 24, TEX_WIDTH - 48, TEX_HEIGHT - 48)

    // 飾りの円（左右どちらのページか判別しやすくする）
    ctx.fillStyle = accentColor
    ctx.globalAlpha = 0.25
    ctx.beginPath()
    ctx.arc(TEX_WIDTH / 2, TEX_HEIGHT * 0.32, 90, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1

    // ページ番号
    ctx.fillStyle = '#4a3f2e'
    ctx.font = 'bold 160px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(label, TEX_WIDTH / 2, TEX_HEIGHT * 0.58)
  })
}

// 画像ページ: contain（切らず全体表示）で中央配置し、余白は紙色で埋める
// 写真の精細さを保つため、このページだけキャンバスを2倍解像度にする
export function createImagePageTexture({ image, bgColor = '#f5efe0', accentColor = '#8a7a5c' }) {
  const W = TEX_WIDTH * 2
  const H = TEX_HEIGHT * 2
  const margin = 48 // 縁取りの内側に収める
  return createCanvasTexture((ctx) => {
    ctx.fillStyle = bgColor
    ctx.fillRect(0, 0, W, H)

    const scale = Math.min((W - margin * 2) / image.width, (H - margin * 2) / image.height)
    const dw = image.width * scale
    const dh = image.height * scale
    ctx.drawImage(image, (W - dw) / 2, (H - dh) / 2, dw, dh)

    // 中ページと同じ縁取り（2倍解像度に合わせて太さも2倍）
    ctx.strokeStyle = accentColor
    ctx.lineWidth = 12
    ctx.strokeRect(margin, margin, W - margin * 2, H - margin * 2)
  }, W, H)
}

// 文字ページ: タイトル＋「ラベル / 本文」の組を縦に並べる。長文は自動折り返し。
// title を省略するとタイトルなし（前ページの続きなど）として上から詰めて描画する
export function createTextPageTexture({ title, items = [], bgColor = '#f5efe0', accentColor = '#8a7a5c' }) {
  return createCanvasTexture((ctx) => {
    ctx.fillStyle = bgColor
    ctx.fillRect(0, 0, TEX_WIDTH, TEX_HEIGHT)

    ctx.strokeStyle = accentColor
    ctx.lineWidth = 6
    ctx.strokeRect(24, 24, TEX_WIDTH - 48, TEX_HEIGHT - 48)

    const marginX = 60
    const maxWidth = TEX_WIDTH - marginX * 2
    ctx.textBaseline = 'alphabetic'
    let y = 110

    if (title) {
      // タイトル＋アクセント下線
      ctx.fillStyle = '#4a3f2e'
      ctx.font = 'bold 52px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(title, TEX_WIDTH / 2, y + 20, maxWidth)
      ctx.strokeStyle = accentColor
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.moveTo(marginX, y + 46)
      ctx.lineTo(TEX_WIDTH - marginX, y + 46)
      ctx.stroke()
      y += 120
    }

    // 各項目: ラベル（小・アクセント色）＋本文（いずれも折り返しあり）
    ctx.textAlign = 'left'
    for (const { label, value } of items) {
      ctx.fillStyle = accentColor
      ctx.font = 'bold 22px sans-serif'
      for (const line of wrapText(ctx, label, maxWidth)) {
        ctx.fillText(line, marginX, y)
        y += 32
      }
      y += 6

      ctx.fillStyle = '#4a3f2e'
      ctx.font = '26px sans-serif'
      for (const line of wrapText(ctx, value, maxWidth)) {
        ctx.fillText(line, marginX, y)
        y += 38
      }
      y += 26 // 項目間の余白
    }
  })
}

// 表紙・裏表紙: 濃色の背景＋タイトル
export function createCoverTexture({ title, subtitle = '', bgColor = '#2c3a55' }) {
  return createCanvasTexture((ctx) => {
    // 上下方向のグラデーションで革表紙っぽく
    const gradient = ctx.createLinearGradient(0, 0, 0, TEX_HEIGHT)
    gradient.addColorStop(0, bgColor)
    gradient.addColorStop(1, '#141b2b')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, TEX_WIDTH, TEX_HEIGHT)

    // 二重の縁取り
    ctx.strokeStyle = '#c9b37e'
    ctx.lineWidth = 8
    ctx.strokeRect(30, 30, TEX_WIDTH - 60, TEX_HEIGHT - 60)
    ctx.lineWidth = 2
    ctx.strokeRect(46, 46, TEX_WIDTH - 92, TEX_HEIGHT - 92)

    ctx.fillStyle = '#e8dcbc'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = 'bold 72px serif'
    ctx.fillText(title, TEX_WIDTH / 2, TEX_HEIGHT * 0.42)

    if (subtitle) {
      ctx.font = '36px serif'
      ctx.fillText(subtitle, TEX_WIDTH / 2, TEX_HEIGHT * 0.56)
    }
  })
}
